import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { studentFormsApi, type StudentFormHistoryEntry } from '@/api/studentForms'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { formatDateTimeDMY } from '@/utils/date'

interface StudentFormHistoryDialogProps {
  open: boolean
  contractId: number
  onClose: () => void
}

/** Значение из истории в читаемый текст: тип зависит от поля анкеты. */
function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? '✓' : '✗'
  if (typeof value === 'object') {
    const pair = value as { from?: string; to?: string }
    if (pair.from || pair.to) return `${pair.from ?? '?'} → ${pair.to ?? '?'}`
    return JSON.stringify(value)
  }
  return String(value)
}

export function StudentFormHistoryDialog({
  open,
  contractId,
  onClose,
}: StudentFormHistoryDialogProps) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [entries, setEntries] = useState<StudentFormHistoryEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(15)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return

    let cancelled = false
    setLoading(true)
    setError(null)

    void (async () => {
      try {
        const result = await studentFormsApi.getHistory(contractId, {
          page: page + 1,
          per_page: perPage,
        })
        if (cancelled) return
        setEntries(result.data)
        setTotal(result.total)
      } catch (err) {
        if (!cancelled) {
          setError(displayErrorMessage(getErrorMessage(err), t('studentForms.historyError')))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, contractId, page, perPage, t])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('studentForms.history')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rectangular" height={40} />
            ))}
          </Box>
        ) : entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t('studentForms.historyEmpty')}
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('studentForms.historyField')}</TableCell>
                    <TableCell>{t('studentForms.historyOld')}</TableCell>
                    <TableCell>{t('studentForms.historyNew')}</TableCell>
                    <TableCell>{t('studentForms.historyDate')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>{entry.field_key}</TableCell>
                      <TableCell>{formatValue(entry.old_value)}</TableCell>
                      <TableCell>{formatValue(entry.new_value)}</TableCell>
                      <TableCell>{formatDateTimeDMY(entry.changed_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={perPage}
                rowsPerPageOptions={[10, 15, 25, 50]}
                onRowsPerPageChange={(e) => {
                  setPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                labelRowsPerPage={t('studentForms.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) =>
                  t('studentForms.displayedRows', { from, to, count })
                }
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close', 'Закрыть')}</Button>
      </DialogActions>
    </Dialog>
  )
}
